import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";
import { ArrowBack, Search } from "@mui/icons-material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import EmptyState from "../../lib/components/EmptyState";
import organizationservice from "../../service/organizationservice";
import transmissionService from "../../service/transmissionService";
import pathnames, { apiTransmissionLogPath } from "../../constants/pathnames";
import AppTableLoader from "../../lib/components/api/AppTableLoader";
import iconTotalApi from "../../assets/transmission/totalapi.png";
import iconSuccess from "../../assets/transmission/success.png";
import iconFailedCalls from "../../assets/transmission/failedrate.png";
import iconSuccessRate from "../../assets/transmission/sucessrate.png";
import iconFailureRate from "../../assets/transmission/rate.png";
import DateRangePreset, {
  buildDateParams,
  DATE_PRESETS,
  extractFilterDates,
  normalizeDatePreset,
} from "./components/DateRangePreset";
import SortFilterMenu from "./components/SortFilterMenu";
import FilterLogsModal, {
  createDefaultLogFilters,
  toLogQueryParams,
} from "./components/FilterLogsModal";
import CustomPagination from "../../lib/components/AppPagination";
import AppTextField from "../../lib/components/AppTextField";
import useDebounce from "../../hooks/useDebounce";

const COLORS = {
  page: "#F5F5F5",
  cardBorder: "#E5E7EB",
  tableHead: "#F6F6F6",
  label: "#6B7280",
  title: "#111827",
  link: "#2563EB",
  success: "#009068",
  successText: "#059669",
  successBg: "#EDFEEC",
  successBorder: "#79C880",
  successTint: "#E5FBF1",
  successTrack: "#D1FAE5",
  danger: "#EF4444",
  dangerBg: "#FEEEF0",
  dangerBorder: "#FF7D7D",
  dangerTint: "#FEE2E2",
  dangerTrack: "#FECACA",
  info: "#2563EB",
  infoBg: "#D7ECFF",
  infoBorder: "#8BB1FF",
  infoTint: "#E6F0FF",
  funnelNeutral: "#F9F9FB",
  pending: "#EA580C",
  pendingBg: "#FFF7ED",
  pendingBorder: "#FDBA74",
  pendingTint: "#FFEDD5",
  purple: "#A855F7",
  purpleStroke: "#8A63FF",
};

const LIFECYCLE_TOOLTIPS = {
  signed: "Total number of invoices signed in this period, broken down by successful and failed signing attempts.",
  initiated: "Total number of transmission initiation attempts in this period, broken down by successful and failed attempts.",
  completed: "Invoices that have been successfully transmitted to and acknowledged by the tax authority (FIRS/NRS).",
  pending: "Invoices that have been submitted for transmission but are still awaiting confirmation from the tax authority.",
  failed: "Invoices that could not be transmitted successfully and require review or resubmission.",
  payment: "Invoices for which payment has been recorded and reported, separate from transmission status.",
};

const cardSx = {
  bgcolor: "#fff",
};

// The API Usage chart scopes the backend monthly-usage series to this month,
// all time, or a user-selected calendar year (drives the year-based custom selector).
const USAGE_PRESET_OPTIONS = [
  { label: "This month", value: DATE_PRESETS.THIS_MONTH },
  { label: "All time", value: DATE_PRESETS.ALL_TIME },
  { label: "Custom", value: DATE_PRESETS.CUSTOM },
];

// Default rows-per-page for the Recent API Logs table.
const LOGS_PAGE_SIZE = 10;
const LOGS_LIMIT_OPTIONS = [10, 20, 50];

const FAILURE_CATEGORIES = [
  { name: "Validation Error", color: "#111111" },
  { name: "Upstream (NRS)", color: "#8B5CF6" },
  { name: "Auth", color: "#FACC15" },
  { name: "Business Guard", color: "#38A543" },
  { name: "Rate Limited", color: "#015AB6" },
  { name: "Unreachable", color: "#38BDF8" },
  { name: "Timeout", color: "#FB6B70" },
];

const ERROR_RATE_THRESHOLD = 4;

/**
 * Normalize a request URL to a path-only endpoint.
 */
function getEndpoint(url = "") {
  if (!url) return "Unknown endpoint";
  try {
    return new URL(url).pathname || url;
  } catch {
    const path = String(url).split("?")[0];
    return path || url;
  }
}

/**
 * Unwrap the analytics endpoints' response body.
 * These endpoints return `{ status: "SUCCESS", data: { ... } }`, i.e. an extra
 * `data` wrapper on top of the axios `data`. Older endpoints return the payload
 * object directly under `data`. This helper handles both shapes so consumers
 * never have to reason about nesting.
 */
function unwrapAnalytics(responseData) {
  const body = responseData?.data;
  if (!body || typeof body !== "object" || Array.isArray(body)) return body || null;
  // Prefer the inner `data` object when the body carries a `status`/`data` wrapper.
  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    return body.data;
  }
  return body;
}

/**
 * Normalize a paginated list payload from analytics/log endpoints.
 * Supports common shapes: `{ data }`, `{ docs }`, `{ results }`, or a bare array,
 * plus totalDocs/totalPages (or total/pages) pagination meta.
 */
function unwrapPaginatedList(responseData, { page = 1, limit = LOGS_PAGE_SIZE } = {}) {
  const payload = unwrapAnalytics(responseData);

  if (!payload) {
    return { data: [], totalDocs: 0, totalPages: 1, page, limit };
  }

  if (Array.isArray(payload)) {
    const totalDocs = payload.length;
    const totalPages = Math.max(1, Math.ceil(totalDocs / limit) || 1);
    const start = (Math.max(1, page) - 1) * limit;
    return {
      data: payload.slice(start, start + limit),
      totalDocs,
      totalPages,
      page,
      limit,
      clientPaginated: true,
    };
  }

  const rows = Array.isArray(payload.data)
    ? payload.data
    : Array.isArray(payload.docs)
      ? payload.docs
      : Array.isArray(payload.results)
        ? payload.results
        : Array.isArray(payload.items)
          ? payload.items
          : [];

  const totalDocs = Number(payload.totalDocs ?? payload.total ?? payload.count ?? rows.length) || 0;
  const pageSize = Number(payload.limit ?? payload.pageSize ?? limit) || limit;
  let totalPages = Number(payload.totalPages ?? payload.pages) || 0;

  // Backend returned more rows than the requested page size and no usable page
  // count — paginate on the client so the table stays bounded.
  if (rows.length > pageSize && totalPages <= 1) {
    const clientTotal = Math.max(totalDocs, rows.length);
    const clientPages = Math.max(1, Math.ceil(clientTotal / pageSize));
    const start = (Math.max(1, page) - 1) * pageSize;
    return {
      data: rows.slice(start, start + pageSize),
      totalDocs: clientTotal,
      totalPages: clientPages,
      page,
      limit: pageSize,
      clientPaginated: true,
    };
  }

  if (!totalPages) {
    totalPages = Math.max(1, Math.ceil(totalDocs / pageSize) || 1);
  }

  return {
    data: rows,
    totalDocs,
    totalPages,
    page: Number(payload.page ?? payload.currentPage ?? page) || page,
    limit: pageSize,
    clientPaginated: false,
  };
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Map a backend monthly-usage item to its 1-based month index (1..12).
 * The backend uses `month: "YYYY-MM"` (and `label: "Aug"`); we also accept a
 * bare month name/label so this stays resilient to shape changes.
 */
function monthIndexOf(item = {}) {
  const raw = String(item?.month || item?.key || "");
  // "YYYY-MM" or "YYYY-M"
  const match = raw.match(/(?:^|\D)(\d{1,2})$/) || raw.match(/^(\d{4})-(\d{1,2})$/);
  if (match) {
    const idx = Number(match[2] ?? match[1]);
    if (idx >= 1 && idx <= 12) return idx;
  }
  // label / name like "Aug"
  const label = String(item?.label || item?.name || "").toLowerCase();
  const byLabel = MONTH_LABELS.findIndex((m, i) => label === m.toLowerCase() || label.includes(m.toLowerCase())) % 12;
  if (byLabel >= 0) return byLabel + 1;
  return 0;
}

/**
 * Front-fill a complete Jan–Dec series for a given year.
 * Months without backend data are zero-filled so the chart always shows the
 * full calendar year (better UX than a chart that only plots months with data).
 */
function buildFullYearUsageSeries(series = [], year = new Date().getFullYear()) {
  const counts = Array(12).fill(0);
  series.forEach((item) => {
    const idx = monthIndexOf(item);
    if (idx >= 1) counts[idx - 1] = Number(item?.count) || 0;
  });
  return counts.map((value, i) => ({
    month: `${year}-${String(i + 1).padStart(2, "0")}`,
    label: MONTH_LABELS[i],
    value,
  }));
}

function isSuccessful(log) {
  const code = Number(log?.statusCode);
  if (Number.isFinite(code) && code > 0) return code >= 200 && code < 300;
  return Boolean(log?.response?.data?.ok);
}

function getStatusCode(log) {
  const code = Number(log?.response?.status || log?.response?.data?.statusCode || log?.response?.statusCode || log?.statusCode);
  if (Number.isFinite(code) && code > 0) return code;
  return isSuccessful(log) ? 200 : 500;
}

/**
 * Map a backend failure category code to the label used in the UI legend.
 * e.g. "VALIDATION" / "VALIDATION_ERROR" -> "Validation Error".
 */
function toFailureLabel(category = "") {
  const normalized = String(category)
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  const alias = {
    VALIDATION: "Validation Error",
    VALIDATION_ERROR: "Validation Error",
    UPSTREAM: "Upstream (NRS)",
    AUTH: "Auth",
    AUTHENTICATION: "Auth",
    BUSINESS_GUARD: "Business Guard",
    BUSINESSGUARD: "Business Guard",
    RATE_LIMITED: "Rate Limited",
    RATE_LIMIT: "Rate Limited",
    TIMEOUT: "Timeout",
    UNREACHABLE: "Unreachable",
  };
  return alias[normalized] || String(category || "Unreachable");
}

const EXTRA_FAILURE_COLORS = ["#64748B", "#0EA5E9", "#F97316", "#84CC16", "#E11D48", "#7C3AED"];

function classifyFailure(log) {
  const code = getStatusCode(log);
  if (code === 429) return "Rate Limited";
  if (code === 401 || code === 403) return "Auth";
  if (code === 408 || code === 504) return "Timeout";
  if (code === 404 || code === 502 || code === 503 || code === 0) return "Unreachable";
  if (code === 400 || code === 422) return "Validation Error";
  if (code >= 500) return "Upstream (NRS)";
  if (code >= 400) return "Business Guard";
  return "Unreachable";
}

function formatBillingInterval(interval) {
  if (!interval) return "N/A";
  return String(interval).charAt(0).toUpperCase() + String(interval).slice(1).toLowerCase();
}

function formatSummaryPercent(value) {
  if (value == null || value === "") return "0%";
  const num = Number(value);
  if (!Number.isFinite(num)) return "0%";
  return `${num}%`;
}

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}

/**
 * Compact count formatting for the E-Invoice lifecycle "Invoices signed" card.
 * Values >= 1000 render as e.g. 1k / 12.1k / 999k and roll up to M / B / T at the
 * next unit (999.9k -> 1M) so the card stays scannable at a glance.
 */
function formatCompactCount(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0";
  const abs = Math.abs(num);
  if (abs < 1000) return String(num);
  const units = [
    { value: 1e12, suffix: "T" },
    { value: 1e9, suffix: "B" },
    { value: 1e6, suffix: "M" },
    { value: 1e3, suffix: "k" },
  ];
  let unit = units.find((u) => abs >= u.value) || units[units.length - 1];
  // If rounding up to one decimal crosses into the next unit (e.g. 999.9k -> 1M),
  // promote so we never render awkward values like "1000k".
  const nextUnit = units[units.indexOf(unit) - 1];
  if (nextUnit) {
    const roundedUp = Number((abs / unit.value).toFixed(1));
    if (roundedUp >= nextUnit.value / unit.value) unit = nextUnit;
  }
  const scaled = num / unit.value;
  const text =
    Math.abs(scaled) >= 100
      ? String(Math.round(scaled))
      : scaled.toFixed(1).replace(/\.0$/, "");
  return `${text}${unit.suffix}`;
}

const ViewApiTransmissionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const urlDatePreset = searchParams.get("datePreset");
  const urlDateFrom = searchParams.get("dateFrom") || null;
  const urlDateTo = searchParams.get("dateTo") || null;
  const [responseFilters, setResponseFilters] = React.useState(null);

  // When the list page opens this view with ALL_TIME, seed every section filter
  // to ALL_TIME so the whole page stays consistent.
  const initialSectionPreset =
    normalizeDatePreset(urlDatePreset) === DATE_PRESETS.ALL_TIME
      ? DATE_PRESETS.ALL_TIME
      : DATE_PRESETS.THIS_MONTH;

  // Independent date scope for the API Usage chart (not tied to the dashboard
  // summary filters). Only THIS_MONTH, ALL_TIME, and a year-based CUSTOM are offered.
  const [usageDatePreset, setUsageDatePreset] = React.useState(initialSectionPreset);
  const [usageDateFrom, setUsageDateFrom] = React.useState(null);
  const [usageDateTo, setUsageDateTo] = React.useState(null);

  // Independent date range for the Failure by Categories chart.
  const [failureDatePreset, setFailureDatePreset] = React.useState(initialSectionPreset);
  const [failureDateFrom, setFailureDateFrom] = React.useState(null);
  const [failureDateTo, setFailureDateTo] = React.useState(null);

  // Independent date range for the Calls by Endpoint (Top 5) table.
  const [endpointDatePreset, setEndpointDatePreset] = React.useState(initialSectionPreset);
  const [endpointDateFrom, setEndpointDateFrom] = React.useState(null);
  const [endpointDateTo, setEndpointDateTo] = React.useState(null);

  // Independent filters, search, and pagination for the Recent API Logs table.
  const [logFilters, setLogFilters] = React.useState(() => createDefaultLogFilters(initialSectionPreset));
  const [logKeyword, setLogKeyword] = React.useState("");
  const debouncedLogKeyword = useDebounce(logKeyword, 800);
  const [logPage, setLogPage] = React.useState(1);
  const [logLimit, setLogLimit] = React.useState(LOGS_PAGE_SIZE);
  const [logSortOrder, setLogSortOrder] = React.useState("desc");

  // Independent date range for the Webhooks table.
  const [webhookDatePreset, setWebhookDatePreset] = React.useState(initialSectionPreset);
  const [webhookDateFrom, setWebhookDateFrom] = React.useState(null);
  const [webhookDateTo, setWebhookDateTo] = React.useState(null);
  // Independent pagination for the Webhooks table.
  const [webhookPage, setWebhookPage] = React.useState(1);
  const [webhookLimit, setWebhookLimit] = React.useState(LOGS_PAGE_SIZE);

  const datePreset = normalizeDatePreset(urlDatePreset || responseFilters?.datePreset || DATE_PRESETS.THIS_MONTH);
  const dateFrom = urlDateFrom || responseFilters?.dateFrom || null;
  const dateTo = urlDateTo || responseFilters?.dateTo || null;
  const dateParams = urlDatePreset ? buildDateParams(urlDatePreset, urlDateFrom, urlDateTo) : {};

  const organizationQuery = useQuery({
    retry: false,
    queryKey: ["organizations-details", id],
    queryFn: () => organizationservice.getOne({ id }),
  });

  const firsIntegrationQuery = useQuery({
    enabled: Boolean(id),
    retry: false,
    queryKey: ["firs-integrations", id],
    queryFn: () => organizationservice.getFirsIntegration({ organizationId: id }),
  });

  const apiLogParams = { organizationId: id, ...dateParams };
  const usageDateParams = buildDateParams(usageDatePreset, usageDateFrom, usageDateTo);
  const failureDateParams = buildDateParams(failureDatePreset, failureDateFrom, failureDateTo);
  const endpointDateParams = buildDateParams(endpointDatePreset, endpointDateFrom, endpointDateTo);
  const logQueryParams = toLogQueryParams(logFilters);
  const webhookDateParams = buildDateParams(webhookDatePreset, webhookDateFrom, webhookDateTo);

  const summaryQuery = useQuery({
    enabled: Boolean(id),
    refetchInterval: 5 * 60 * 1000,
    queryKey: ["api-request-logs-summary", id, ...Object.values(dateParams)],
    queryFn: () => transmissionService.getApiRequestLogsSummary(apiLogParams),
  });

  const topEndpointsQuery = useQuery({
    enabled: Boolean(id),
    refetchInterval: 5 * 60 * 1000,
    queryKey: ["api-request-logs-top-endpoints", id, ...Object.values(endpointDateParams)],
    queryFn: () => transmissionService.getApiRequestLogsTopEndpoints({ organizationId: id, ...endpointDateParams }),
  });

  const failuresQuery = useQuery({
    enabled: Boolean(id),
    refetchInterval: 5 * 60 * 1000,
    queryKey: ["api-request-logs-failures", id, ...Object.values(failureDateParams)],
    queryFn: () => transmissionService.getApiRequestLogsFailuresByCategory({ organizationId: id, ...failureDateParams }),
  });

  const monthlyUsageQuery = useQuery({
    enabled: Boolean(id),
    refetchInterval: 5 * 60 * 1000,
    queryKey: ["api-request-logs-monthly-usage", id, ...Object.values(usageDateParams)],
    queryFn: () => transmissionService.getApiRequestLogsMonthlyUsage({ organizationId: id, ...usageDateParams }),
  });

  const logsQuery = useQuery({
    enabled: Boolean(id),
    refetchInterval: 5 * 60 * 1000,
    queryKey: ["api-request-logs", id, logSortOrder, logPage, logLimit, debouncedLogKeyword, logQueryParams],
    queryFn: () =>
      transmissionService.getApiRequestLogs({
        organizationId: id,
        page: logPage,
        limit: logLimit,
        sortOrder: logSortOrder,
        search: debouncedLogKeyword || undefined,
        ...logQueryParams,
      }),
  });

  const webhooksQuery = useQuery({
    enabled: Boolean(id),
    refetchInterval: 5 * 60 * 1000,
    queryKey: ["api-request-logs-webhooks", id, webhookPage, webhookLimit, ...Object.values(webhookDateParams)],
    queryFn: () =>
      transmissionService.getApiRequestLogsWebhooks({
        organizationId: id,
        page: webhookPage,
        limit: webhookLimit,
        ...webhookDateParams,
      }),
  });

  const summaryFilters = unwrapAnalytics(summaryQuery.data)?.filters;

  React.useEffect(() => {
    // The summary endpoint returns the authoritative date filter used for the
    // KPI/funnel cards and top-of-page dashboard control.
    const filters = summaryFilters;
    if (!filters) return;
    setResponseFilters(extractFilterDates(filters));
  }, [summaryFilters]);

  const organization = organizationQuery.data?.data?.data;
  const transmission = firsIntegrationQuery.data?.data?.data?.[0];
  const result = unwrapAnalytics(logsQuery.data);
  const logs = result?.data || [];

  const webhookResult = unwrapPaginatedList(webhooksQuery.data, {
    page: webhookPage,
    limit: webhookLimit,
  });
  const webhooks = webhookResult.data;

  // ---- Summary analytics (server-side; display values as returned) ----
  const summaryData = unwrapAnalytics(summaryQuery.data)?.summary || {};
  const summaryCardsLoading = summaryQuery.isPending || summaryQuery.isFetching;
  const totalCalls = summaryData.totalApiCalls ?? 0;
  const successfulCalls = summaryData.totalSuccessfulCalls ?? 0;
  const failedCalls = summaryData.totalFailedCalls ?? 0;
  const successRate = summaryData.successRate ?? 0;
  const failureRate = summaryData.errorRate ?? 0;

  // E-Invoice lifecycle funnel reads directly from the summary endpoint.
  const signedTotal = Number(summaryData.totalInvoicesSigned ?? 0);
  const signedSuccessful = Number(summaryData.totalInvoicesSignedSuccessful ?? 0);
  const signedFailed = Number(summaryData.totalInvoicesSignedFailed ?? 0);
  const initiationTotal = Number(summaryData.totalTransmissionInitiation ?? 0);
  const initiationSuccessful = Number(summaryData.totalTransmissionInitiationSuccessful ?? 0);
  const initiationFailed = Number(summaryData.totalTransmissionInitiationFailed ?? 0);
  const transmissionCompleted = Number(summaryData.totalTransmissionInitiationCompleted ?? 0);
  const transmissionPending = Number(summaryData.totalTransmissionInitiationPending ?? 0);
  const transmissionFailed = Number(summaryData.totalTransmissionInitiationFailed ?? 0);
  const paymentReported = Number(summaryData.totalPaymentReported ?? 0);

  // ---- Monthly usage (server-side series) ----
  // Phase the partial series into a full Jan–Dec series so the chart always
  // shows all 12 calendar months with counts mapped onto each.
  const rawSeries = unwrapAnalytics(monthlyUsageQuery.data)?.series || [];
  const usageYear =
    (rawSeries[0] && Number(String(rawSeries[0]?.month || rawSeries[0]?.key || "").split("-")[0])) ||
    (usageDateFrom ? moment(usageDateFrom).year() : new Date().getFullYear());
  const apiUsage = buildFullYearUsageSeries(rawSeries, usageYear);

  // ---- Top endpoints ----
  // Backend rows when present; client-side derivation as a rollout fallback.
  const backendEndpointRows = (unwrapAnalytics(topEndpointsQuery.data)?.rows || []).map((row) => ({
    endpoint: row?.endpoint,
    requests: Number(row?.requestCount) || 0,
    latency: Number(row?.averageLatencyMs) || 0,
    successes: Number(row?.successCount) || 0,
    successRate: row?.successRate ?? 0,
    errors: Number(row?.errorCount) || 0,
    errorRate: row?.errorRate ?? 0,
  }));
  const derivedEndpointRows = Object.values(
    logs.reduce((groups, log) => {
      const endpoint = getEndpoint(log?.transportData?.url || log?.endpoint);
      const item = groups[endpoint] || { endpoint, requests: 0, duration: 0, successes: 0, errors: 0 };
      item.requests += 1;
      item.duration += Number(log?.durationMs) || 0;
      if (isSuccessful(log)) item.successes += 1;
      else item.errors += 1;
      groups[endpoint] = item;
      return groups;
    }, {}),
  )
    .map((item) => ({
      ...item,
      latency: item.requests ? item.duration / item.requests : 0,
      successRate: item.requests ? (item.successes / item.requests) * 100 : 0,
      errorRate: item.requests ? (item.errors / item.requests) * 100 : 0,
    }))
    .sort((a, b) => b.requests - a.requests)
    .slice(0, 5);
  const endpointRows = backendEndpointRows.length ? backendEndpointRows : derivedEndpointRows;

  // ---- Failures by category ----
  // Render only the categories returned by the backend (mapped to UI labels), and
  // reuse the static FE palette per label (EXTRA_FAILURE_COLORS as a fallback for
  // labels not in the palette). No pre-baked zero-count legend entries.
  const failureCategories = unwrapAnalytics(failuresQuery.data)?.categories || [];
  const failureTotalFromBackend = Number(unwrapAnalytics(failuresQuery.data)?.totalFailures) ||
    failureCategories.reduce((sum, row) => sum + Number(row?.count || 0), 0);
  const fallbackFailureCounts = !failureCategories.length
    ? logs.filter((log) => !isSuccessful(log)).reduce((groups, log) => {
        const label = toFailureLabel(classifyFailure(log));
        groups[label] = (groups[label] || 0) + 1;
        return groups;
      }, {})
    : {};
  const categoryMap = failureCategories.reduce((acc, row) => {
    const label = toFailureLabel(row?.category);
    acc[label] = (acc[label] || 0) + Number(row?.count || 0);
    return acc;
  }, fallbackFailureCounts);
  const failureData = Object.entries(categoryMap).map(([name, value], index) => {
    const paletteMatch = FAILURE_CATEGORIES.find((item) => item.name === name);
    return {
      name,
      value: Number(value || 0),
      color: paletteMatch?.color || EXTRA_FAILURE_COLORS[index % EXTRA_FAILURE_COLORS.length],
    };
  });
  const failureTotal = Number(failureTotalFromBackend) ||
    failureData.reduce((sum, item) => sum + item.value, 0);

  const healthCards = [
    {
      label: "Total API Calls",
      value: formatCount(totalCalls),
      subtitle: "All external FIRS endpoints",
      icon: iconTotalApi,
      color: COLORS.info,
      tint: COLORS.infoTint,
    },
    {
      label: "Successful Calls",
      value: formatCount(successfulCalls),
      subtitle: "2xx Status codes",
      rate: formatSummaryPercent(successRate),
      rateColor: COLORS.success,
      icon: iconSuccess,
      color: COLORS.success,
      tint: COLORS.successTint,
    },
    {
      label: "Failed Calls",
      value: formatCount(failedCalls),
      subtitle: "4xx & 5xx Status codes",
      rate: formatSummaryPercent(failureRate),
      rateColor: COLORS.danger,
      icon: iconFailedCalls,
      color: COLORS.danger,
      tint: COLORS.dangerTint,
    },
    {
      label: "Success Rate",
      value: formatSummaryPercent(successRate),
      progress: successRate,
      icon: iconSuccessRate,
      color: COLORS.success,
      iconColor: COLORS.info,
      tint: COLORS.infoTint,
      track: COLORS.successTrack,
    },
    {
      label: "Failure Rate",
      value: formatSummaryPercent(failureRate),
      progress: failureRate,
      icon: iconFailureRate,
      color: COLORS.danger,
      tint: COLORS.dangerTint,
      track: COLORS.dangerTrack,
    },
  ];

  const lifecycleStatusColumns = [
    [
      {
        label: "Transmission Completed",
        value: formatCount(transmissionCompleted),
        tooltip: LIFECYCLE_TOOLTIPS.completed,
        color: COLORS.successText,
        border: "#86EFAC",
      },
      {
        label: "Transmission Pending",
        value: formatCount(transmissionPending),
        tooltip: LIFECYCLE_TOOLTIPS.pending,
        color: COLORS.pending,
        border: "#FDBA74",
      },
    ],
    [
      {
        label: "Transmission Failed",
        value: formatCount(transmissionFailed),
        tooltip: LIFECYCLE_TOOLTIPS.failed,
        color: COLORS.danger,
        border: "#FCA5A5",
      },
      {
        label: "Payment Reported",
        value: formatCount(paymentReported),
        tooltip: LIFECYCLE_TOOLTIPS.payment,
        color: COLORS.info,
        border: COLORS.cardBorder,
      },
    ],
  ];

  function updateSearchParams(mutator) {
    const next = new URLSearchParams(window.location.search);
    mutator(next);
    navigate({ pathname: window.location.pathname, search: next.toString() }, { replace: true });
  }

  function handleLogSortOrderChange(eventOrValue) {
    const value = eventOrValue?.target ? eventOrValue.target.value : eventOrValue;
    setLogSortOrder(value);
    setLogPage(1);
  }

  function applyAllTimeToSections() {
    setUsageDatePreset(DATE_PRESETS.ALL_TIME);
    setUsageDateFrom(null);
    setUsageDateTo(null);
    setFailureDatePreset(DATE_PRESETS.ALL_TIME);
    setFailureDateFrom(null);
    setFailureDateTo(null);
    setEndpointDatePreset(DATE_PRESETS.ALL_TIME);
    setEndpointDateFrom(null);
    setEndpointDateTo(null);
    setLogFilters((prev) => ({
      ...prev,
      datePreset: DATE_PRESETS.ALL_TIME,
      dateFrom: null,
      dateTo: null,
    }));
    setLogPage(1);
    setWebhookDatePreset(DATE_PRESETS.ALL_TIME);
    setWebhookDateFrom(null);
    setWebhookDateTo(null);
    setWebhookPage(1);
  }

  function handlePresetChange(preset) {
    updateSearchParams((params) => {
      params.set("datePreset", preset);
      if (preset !== DATE_PRESETS.CUSTOM) {
        params.delete("dateFrom");
        params.delete("dateTo");
      }
    });
    // Keep every section filter on ALL_TIME when the page-level control selects it.
    if (preset === DATE_PRESETS.ALL_TIME) {
      applyAllTimeToSections();
    }
  }

  function handleCustomRangeChange({ dateFrom: from, dateTo: to }) {
    updateSearchParams((params) => {
      params.set("datePreset", DATE_PRESETS.CUSTOM);
      params.set("dateFrom", from);
      params.set("dateTo", to);
    });
  }

  // Independent handlers for the API Usage chart's own date range.
  function handleUsagePresetChange(preset) {
    setUsageDatePreset(preset);
    setUsageDateFrom(null);
    setUsageDateTo(null);
  }

  function handleUsageCustomRangeChange({ dateFrom: from, dateTo: to }) {
    setUsageDatePreset(DATE_PRESETS.CUSTOM);
    setUsageDateFrom(from);
    setUsageDateTo(to);
  }

  // Independent handlers for the Failure by Categories chart's own date range.
  function handleFailurePresetChange(preset) {
    setFailureDatePreset(preset);
    setFailureDateFrom(null);
    setFailureDateTo(null);
  }

  function handleFailureCustomRangeChange({ dateFrom: from, dateTo: to }) {
    setFailureDatePreset(DATE_PRESETS.CUSTOM);
    setFailureDateFrom(from);
    setFailureDateTo(to);
  }

  // Independent handlers for the Calls by Endpoint (Top 5) table's date range.
  function handleEndpointPresetChange(preset) {
    setEndpointDatePreset(preset);
    setEndpointDateFrom(null);
    setEndpointDateTo(null);
  }

  function handleEndpointCustomRangeChange({ dateFrom: from, dateTo: to }) {
    setEndpointDatePreset(DATE_PRESETS.CUSTOM);
    setEndpointDateFrom(from);
    setEndpointDateTo(to);
  }

  function handleApplyLogFilters(nextFilters) {
    setLogFilters(nextFilters);
    setLogPage(1);
  }

  // Independent handlers for the Webhooks table's date range.
  function handleWebhookPresetChange(preset) {
    setWebhookDatePreset(preset);
    setWebhookDateFrom(null);
    setWebhookDateTo(null);
    setWebhookPage(1);
  }

  function handleWebhookCustomRangeChange({ dateFrom: from, dateTo: to }) {
    setWebhookDatePreset(DATE_PRESETS.CUSTOM);
    setWebhookDateFrom(from);
    setWebhookDateTo(to);
    setWebhookPage(1);
  }

  return (
    <AppLayout>
      <Header title="FIRS Transmissions" description="Get insights to Firs Transmissions on here" />

      <Box className="p-3 bg-light" sx={{ minHeight: "100%" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
          <IconButton className="bg-secondary-subtle" onClick={() => navigate(-1)} aria-label="Go back">
            <ArrowBack />
          </IconButton>
          <Box
            display="inline-flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: "pointer", color: COLORS.link }}
            onClick={() => navigate(`${pathnames.TRANSMISSIONS}/${id}`)}
          >
            <Typography fontSize={14} fontWeight={500}>
              Switch Dashboard
            </Typography>
            <CompareArrowsIcon sx={{ fontSize: 24 }} />
          </Box>
        </Box>

        {/* Organization summary */}
        <Paper sx={{ ...cardSx, p: 2, mb: 3 }}>
          {organizationQuery.isPending ? (
            <>
              <Skeleton width={220} height={36} sx={{ mb: 2 }} />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(7, minmax(0, 1fr))" },
                  columnGap: { xs: 2, md: 2.5 },
                  rowGap: 2,
                }}
              >
                {Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton key={index} height={48} />
                ))}
              </Box>
            </>
          ) : (
            <>
              <Typography fontWeight={500} fontSize={20} color={COLORS.title} mb={2} lineHeight={1.2}>
                {organization?.name || "Organization"}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(7, minmax(0, 1fr))" },
                  columnGap: { xs: 2, md: 2.5 },
                  rowGap: 2.5,
                }}
              >
                <Detail label="Join date" value={organization?.createdAt ? moment(organization.createdAt).format("M/D/YYYY") : "N/A"} />
                <Detail label="TIN" value={organization?.firstin || "N/A"} />
                <Detail
                  label="Email"
                  value={organization?.email || "N/A"}
                  link={organization?.email ? `mailto:${organization.email}` : undefined}
                  sx={{ gridColumn: { xs: "auto", md: "span 2" } }}
                />
                <Detail label="Subscription Period" value={formatBillingInterval(organization?.subscription?.billingInterval)} />
                <Detail
                  label="Next Due date"
                  value={organization?.subscription?.endAt ? moment(organization.subscription.endAt).format("M/D/YYYY") : "N/A"}
                />
                <Detail label="ERP" value={transmission?.data?.erp || "N/A"} />
              </Box>
            </>
          )}
        </Paper>

        {/* Health & Volume KPIs */}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} mb={2}>
          <Typography fontSize={20} fontWeight={500} color={COLORS.title}>
            Health &amp; Volume KPIs
          </Typography>
          <DateRangePreset
            datePreset={datePreset}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onPresetChange={handlePresetChange}
            onCustomRangeChange={handleCustomRangeChange}
          />
        </Box>
        <Grid container spacing={2} mb={3}>
          {healthCards.map((card) => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={card.label}>
              <HealthCard {...card} isLoading={summaryCardsLoading} />
            </Grid>
          ))}
        </Grid>

        {/* E-Invoice Lifecycle — 4 equal columns matching design:
            Signed | Initiated | Completed+Pending | Failed+Payment */}
        <SectionTitle>E-Invoice Lifecycle</SectionTitle>
        <Box
          mb={3}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          <LifecycleBreakdownCard
            title="Invoices signed"
            total={signedTotal}
            successful={signedSuccessful}
            failed={signedFailed}
            tooltip={LIFECYCLE_TOOLTIPS.signed}
            dotColor={COLORS.info}
            isLoading={summaryCardsLoading}
          />
          <LifecycleBreakdownCard
            title="Transmission Initiated"
            total={initiationTotal}
            successful={initiationSuccessful}
            failed={initiationFailed}
            tooltip={LIFECYCLE_TOOLTIPS.initiated}
            dotColor={COLORS.info}
            isLoading={summaryCardsLoading}
          />
          {lifecycleStatusColumns.map((column, colIdx) => (
            <Box
              key={colIdx}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "100%",
                minHeight: 0,
              }}
            >
              {column.map((metric) => (
                <LifecycleMetricCard
                  key={metric.label}
                  {...metric}
                  isLoading={summaryCardsLoading}
                />
              ))}
            </Box>
          ))}
        </Box>

        {/* Calls by Endpoint */}
        <Paper sx={{ ...cardSx, p: 2, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" gap={2} mb={2} alignItems="flex-start" flexWrap="wrap">
            <Box>
              <Typography fontSize={20} fontWeight={500} color={COLORS.title}>
                Calls by Endpoint (Top 5)
              </Typography>
            
            </Box>
            <DateRangePreset
              datePreset={endpointDatePreset}
              dateFrom={endpointDateFrom}
              dateTo={endpointDateTo}
              onPresetChange={handleEndpointPresetChange}
              onCustomRangeChange={handleEndpointCustomRangeChange}
            />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: COLORS.tableHead }}>
                <TableRow>
                  {["Endpoint", "Request", "Average Latency", "Success", "Success Rates", "Errors", "Error Rates"].map((name) => (
                    <TableCell key={name} sx={{ py: 1.25 }}>
                      {name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <AppTableLoader cols={7} items={endpointRows} isLoading={topEndpointsQuery.isPending} />
                {endpointRows.map((row) => {
                  const isHighError = row.errorRate >= ERROR_RATE_THRESHOLD;
                  return (
                    <TableRow key={row.endpoint}>
                      <TableCell sx={{ color: COLORS.title }}>{row.endpoint}</TableCell>
                      <TableCell sx={{ color: COLORS.title }}>{row.requests}</TableCell>
                      <TableCell>
                        <LatencyBadge value={row.latency} danger={isHighError} />
                      </TableCell>
                      <TableCell sx={{ color: COLORS.title }}>{row.successes}</TableCell>
                      <TableCell sx={{ color: "#009B69", fontWeight: 500 }}>
                        {formatSummaryPercent(row.successRate)}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.title }}>{row.errors}</TableCell>
                      <TableCell sx={{ color: "#EF2525", fontWeight: 500 }}>
                        {formatSummaryPercent(row.errorRate)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <EmptyState isEmpty={!topEndpointsQuery.isPending && !endpointRows.length} />
        </Paper>

        {/* Webhooks */}
        <Paper sx={{ ...cardSx, p: 2, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1.5} mb={2}>
            <Box>
              <Typography fontSize={20} fontWeight={500} color={COLORS.title}>
                Webhooks
              </Typography>
              <Typography color={COLORS.label} fontSize={14}>
                Last {webhookResult.totalDocs || webhooks.length || 0} webhook deliveries (refreshed every 5 minutes)
              </Typography>
            </Box>
            <DateRangePreset
              datePreset={webhookDatePreset}
              dateFrom={webhookDateFrom}
              dateTo={webhookDateTo}
              onPresetChange={handleWebhookPresetChange}
              onCustomRangeChange={handleWebhookCustomRangeChange}
            />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: COLORS.tableHead }}>
                <TableRow>
                  <TableCell sx={{ width: 60, py: 1.25 }}>
                    S/N
                  </TableCell>
                  {["Time", "IRN", "IRN Status", "Status", "Comment"].map((name) => (
                    <TableCell key={name} sx={{ py: 1.25 }}>
                      {name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <AppTableLoader cols={6} items={webhooks} isLoading={webhooksQuery.isPending} />
                {webhooks.map((item, index) => {
                  const serialNumber = (webhookPage - 1) * webhookLimit + index + 1;
                  return (
                    <TableRow key={item?.id || `${item?.irn || "webhook"}-${serialNumber}`}>
                      <TableCell sx={{ whiteSpace: "nowrap", color: COLORS.title }}>
                        {serialNumber}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", color: COLORS.title }}>
                        {moment(item?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.title, fontWeight: 500, maxWidth: 320 }}>
                        <Typography noWrap fontSize="inherit" fontWeight="inherit" title={item?.irn}>
                          {item?.irn || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <WebhookStatusBadge value={item?.irnStatus} />
                      </TableCell>
                      <TableCell>
                        <WebhookStatusBadge value={item?.status} />
                      </TableCell>
                      <TableCell sx={{ color: COLORS.label, maxWidth: 300 }}>
                        <Typography noWrap fontSize="inherit" title={item?.comment}>
                          {item?.comment || "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <EmptyState isEmpty={!webhooksQuery.isPending && !webhooks.length} />
          {!webhooksQuery.isPending && Boolean(webhookResult.totalDocs || webhooks.length) && (
            <CustomPagination
              page={webhookPage}
              count={Math.max(1, webhookResult.totalPages || 1)}
              limit={webhookLimit}
              limitOptions={LOGS_LIMIT_OPTIONS}
              onPageChange={(event, value) => setWebhookPage(value)}
              onLimitChange={(newLimit) => {
                setWebhookLimit(newLimit);
                setWebhookPage(1);
              }}
            />
          )}
        </Paper>

        {/* Charts */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} lg={7}>
            <Paper sx={{ ...cardSx, p: 2, height: "100%" }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1.5}
                sx={{ width: "100%", minHeight: 40 }}
              >
                <Typography
                  fontWeight={500}
                  fontSize={20}
                  color={COLORS.title}
                  sx={{ flex: "1 1 auto", minWidth: 0, lineHeight: 1.3 }}
                >
                  API Usage
                </Typography>
                <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>
                  <DateRangePreset
                
                    datePreset={usageDatePreset}
                    dateFrom={usageDateFrom}
                    dateTo={usageDateTo}
                    presetOptions={USAGE_PRESET_OPTIONS}
                    customSelector="year"
                    onPresetChange={handleUsagePresetChange}
                    onCustomRangeChange={handleUsageCustomRangeChange}
                  />
                </Box>
              </Box>
              <Box height={280} mt={2}>
                {monthlyUsageQuery.isPending ? (
                  <Skeleton variant="rounded" height="100%" />
                ) : apiUsage.length ? (
                  <ResponsiveContainer>
                    <AreaChart data={apiUsage} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                      <defs>
                        <linearGradient id="usageGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={COLORS.purple} stopOpacity={0.9} />
                          <stop offset="100%" stopColor={COLORS.purple} stopOpacity={0.15} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="4 4" stroke="#E5E7EB" vertical={false} />
                      <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: COLORS.label, fontSize: 12 }} interval={0} minTickGap={0} tickMargin={8} />
                      <YAxis tickLine={false} axisLine={false} tick={{ fill: COLORS.label, fontSize: 12 }} allowDecimals={false} />
                      <Tooltip cursor={{ stroke: "#D1D5DB", strokeDasharray: "4 4" }} />
                      <Area
                        type="monotone"
                        dataKey="value"
                        name="API Calls"
                        stroke={COLORS.purple}
                        strokeWidth={2}
                        fill="url(#usageGradient)"
                        activeDot={{ r: 4 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState isEmpty />
                )}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Paper sx={{ ...cardSx, p: 2, height: "100%", overflow: "hidden", minWidth: 0 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1.5}
                mb={1}
                sx={{ width: "100%", minHeight: 40 }}
              >
                <Typography
                  fontWeight={500}
                  fontSize={20}
                  color={COLORS.title}
                  sx={{ flex: "1 1 auto", minWidth: 0, lineHeight: 1.3 }}
                >
                  Failure by Categories
                </Typography>
                <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>
                  <DateRangePreset
                    compact
                    datePreset={failureDatePreset}
                    dateFrom={failureDateFrom}
                    dateTo={failureDateTo}
                    onPresetChange={handleFailurePresetChange}
                    onCustomRangeChange={handleFailureCustomRangeChange}
                  />
                </Box>
              </Box>
              {failuresQuery.isPending ? (
                <Box
                  sx={{
                    minHeight: 280,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Skeleton variant="circular" width={176} height={176} />
                </Box>
              ) : !failureData.length ? (
                <Box
                  sx={{
                    minHeight: 280,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <EmptyState isEmpty message="No failure categories found" />
                </Box>
              ) : (
              <Box
                sx={{
                  minHeight: 280,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "flex-start", sm: "space-between" },
                  flexDirection: { xs: "column", sm: "row" },
                  width: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                  gap: { xs: 2, sm: 0 },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: { xs: "100%", sm: "50%" },
                    flex: { xs: "none", sm: "0 0 50%" },
                    maxWidth: "100%",
                    height: 220,
                    mx: { xs: "auto", sm: 0 },
                  }}
                >
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={failureData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={62}
                        outerRadius={88}
                        paddingAngle={1}
                        startAngle={90}
                        endAngle={-270}
                        stroke="#fff"
                        strokeWidth={2}
                      >
                        {failureData.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <Typography fontWeight={500} fontSize={24} color={COLORS.title} lineHeight={1.1}>
                      {formatCount(failureTotal)}
                    </Typography>
                    <Typography fontSize={13} color={COLORS.label}>
                      Total
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: { xs: "100%", sm: "40%" },
                    flex: { xs: "none", sm: "0 0 40%" },
                    minWidth: 0,
                    maxWidth: "100%",
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto",
                    columnGap: 1,
                    rowGap: 0.85,
                    alignContent: "center",
                    alignItems: "start",
                  }}
                >
                  {failureData.map((item) => (
                    <React.Fragment key={item.name}>
                      <Box
                        width={9}
                        height={9}
                        borderRadius="50%"
                        bgcolor={item.color}
                        flexShrink={0}
                        mt={0.55}
                      />
                      <Typography
                        fontSize={12}
                        color={COLORS.title}
                        sx={{
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                          lineHeight: 1.35,
                          minWidth: 0,
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography
                        fontSize={12}
                        color={COLORS.title}
                        sx={{ whiteSpace: "nowrap", flexShrink: 0, textAlign: "right", pl: 1 }}
                      >
                        {item.value}({failureTotal ? ((item.value / failureTotal) * 100).toFixed(1) : "0.0"}%)
                      </Typography>
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Recent API Logs */}
        <Paper sx={{ ...cardSx, p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1.5} mb={2}>
            <Box>
              <Typography fontSize={20} fontWeight={500} color={COLORS.title}>
                Recent API Logs
              </Typography>
              <Typography color={COLORS.label} fontSize={14}>
                Last {result?.totalDocs || logs.length || 0} requests (refreshed every 5 minutes)
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <Box sx={{ width: { xs: "100%", sm: 260 } }}>
                <AppTextField
                  placeholder="Search logs..."
                  value={logKeyword}
                  onChange={(event) => {
                    setLogKeyword(event.target.value);
                    setLogPage(1);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search className="text-secondary" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <SortFilterMenu
                hideSortBy
                sortOrder={logSortOrder}
                onSortOrderChange={handleLogSortOrderChange}
              />
              <FilterLogsModal
                filters={logFilters}
                defaultDatePreset={initialSectionPreset}
                onApply={handleApplyLogFilters}
              />
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: COLORS.tableHead }}>
                <TableRow>
                  <TableCell sx={{ width: 60, py: 1.25 }}>
                    S/N
                  </TableCell>
                  {["Time", "Method", "Endpoint", "Status", "Duration", "Action"].map((name) => (
                    <TableCell key={name} sx={{ py: 1.25 }}>
                      {name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <AppTableLoader cols={7} items={logs} isLoading={logsQuery.isPending} />
                {logs.map((item, index) => {
                  const statusCode = getStatusCode(item);
                  const serialNumber = (logPage - 1) * logLimit + index + 1;
                  return (
                    <TableRow key={item?.id}>
                      <TableCell sx={{ whiteSpace: "nowrap", color: COLORS.title }}>
                        {serialNumber}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", color: COLORS.title }}>
                        {moment(item?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                      </TableCell>
                      <TableCell>
                        <MethodBadge method={item?.method || item?.transportData?.method} />
                      </TableCell>
                      <TableCell sx={{ color: COLORS.title }}>{getEndpoint(item?.endpoint || item?.transportData?.url)}</TableCell>
                      <TableCell>
                        <StatusBadge code={statusCode} />
                      </TableCell>
                      <TableCell sx={{ color: COLORS.title }}>{item?.durationMs ?? 0} ms</TableCell>
                      <TableCell>
                        <Box
                          component={Link}
                          to={apiTransmissionLogPath(id, item?.id)}
                          state={{ log: item }}
                          sx={{
                            color: COLORS.link,
                            textDecoration: "underline",
                            fontWeight: 500,
                            fontSize: 14,
                            fontFamily: "inherit",
                          }}
                        >
                          View
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <EmptyState isEmpty={!logsQuery.isPending && !logs.length} />
          {!logsQuery.isPending && Boolean(result?.totalDocs || logs.length) && (
            <CustomPagination
              page={logPage}
              count={result?.totalPages || 1}
              limit={logLimit}
              limitOptions={LOGS_LIMIT_OPTIONS}
              onPageChange={(event, value) => setLogPage(value)}
              onLimitChange={(newLimit) => {
                setLogLimit(newLimit);
                setLogPage(1);
              }}
            />
          )}
        </Paper>
      </Box>
    </AppLayout>
  );
};

const Detail = ({ label, value, link, sx }) => (
  <Box sx={{ minWidth: 0, ...sx }}>
    <Typography color={COLORS.label} fontSize={14} mb={0.4}>
      {label}:
    </Typography>
    {link ? (
      <Typography
        component="a"
        href={link}
        fontWeight={500}
        fontSize={16}
        sx={{ color: COLORS.link, textDecoration: "underline", overflowWrap: "anywhere" }}
      >
        {value}
      </Typography>
    ) : (
      <Typography fontWeight={500} fontSize={16} color={COLORS.title} sx={{ overflowWrap: "anywhere" }}>
        {value}
      </Typography>
    )}
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography fontSize={20} fontWeight={500} color={COLORS.title} mb={2}>
    {children}
  </Typography>
);

const CardIcon = ({ src, alt, size = 20 }) => (
  <Box
    component="img"
    src={src}
    alt={alt}
    sx={{
      width: size,
      height: size,
      objectFit: "contain",
      display: "block",
    }}
  />
);

const HealthCard = ({
  label,
  value,
  subtitle,
  rate,
  rateColor,
  progress,
  icon,
  color,
  iconColor,
  tint,
  track,
  isLoading = false,
}) => (
  <Paper sx={{ ...cardSx, p: 2, minHeight: 164, height: "100%" }}>
    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
      <Typography color={COLORS.label}>
        {label}
      </Typography>
      <Box
        sx={{
          bgcolor: tint,
          color: iconColor || color,
          borderRadius: "50%",
          width: 40,
          height: 40,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        <CardIcon src={icon} alt={label} size={20} />
      </Box>
    </Box>
    {isLoading ? (
      <Skeleton variant="text" width="55%" height={36} sx={{ mt: 1.25 }} />
    ) : (
      <Typography fontWeight={500} fontSize={24} color={COLORS.title} mt={1.25} lineHeight={1.2}>
        {value}
      </Typography>
    )}
    {progress !== undefined ? (
      isLoading ? (
        <Skeleton variant="rounded" height={10} sx={{ mt: 3, borderRadius: 5 }} />
      ) : (
        <LinearProgress
          variant="determinate"
          value={Math.max(0, Math.min(100, progress))}
          sx={{
            mt: 3,
            height: 10,
            borderRadius: 5,
            bgcolor: track || tint,
            "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 5 },
          }}
        />
      )
    ) : (
      <Box mt={1.5} pt={1.25} borderTop={`1px solid ${COLORS.cardBorder}`} display="flex" justifyContent="space-between" gap={1}>
        <Typography color={COLORS.label} fontSize={12}>
          {subtitle}
        </Typography>
        {isLoading ? (
          <Skeleton variant="text" width={40} height={18} />
        ) : rate ? (
          <Typography color={rateColor} fontSize={12} fontWeight={500}>
            {rate}
          </Typography>
        ) : null}
      </Box>
    )}
  </Paper>
);

const LifecycleInfoIcon = ({ title, color }) => (
  <MuiTooltip title={title} arrow placement="top">
    <InfoOutlinedIcon
      sx={{
        fontSize: 14,
        color: color || COLORS.label,
        cursor: "help",
        flexShrink: 0,
      }}
    />
  </MuiTooltip>
);

const LifecycleDot = ({ color }) => (
  <Box
    component="span"
    sx={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      bgcolor: color,
      flexShrink: 0,
      display: "inline-block",
    }}
  />
);

/**
 * Full-height breakdown card (cols 1–2): total + nested SUCCESS / FAILED.
 */
const LifecycleBreakdownCard = ({
  title,
  total,
  successful,
  failed,
  tooltip,
  dotColor = COLORS.info,
  isLoading = false,
}) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 1,
      boxShadow: 1,
      bgcolor: "#FFFFFF",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      minHeight: { xs: 0, md: 240 },
    }}
  >
    <Box display="flex" alignItems="center" gap={0.75} mb={1}>
      <LifecycleDot color={dotColor} />
      <Typography
        fontSize={12}
        fontWeight={500}
        color={COLORS.title}
        letterSpacing="0.04em"
        textTransform="uppercase"
        sx={{ flex: 1, lineHeight: 1.3 }}
      >
        {title}
      </Typography>
      <LifecycleInfoIcon title={tooltip} />
    </Box>

    {isLoading ? (
      <Skeleton variant="text" width="70%" height={34} sx={{ mb: 1.25 }} />
    ) : (
      <Typography
        fontWeight={500}
        fontSize={24}
        color={COLORS.title}
        lineHeight={1.15}
        mb={1.25}
      >
        {formatCount(total)}
      </Typography>
    )}

    <Box display="flex" flexDirection="column" gap={0.75} mt="auto">
      {[
        {
          label: "Success",
          value: successful,
          color: COLORS.successText,
          border: "#86EFAC",
        },
        {
          label: "Failed",
          value: failed,
          color: COLORS.danger,
          border: "#FCA5A5",
        },
      ].map((item) => (
        <Box
          key={item.label}
          sx={{
            px: 1.25,
            py: 0.85,
            borderRadius: 1,
            border: `1px solid ${item.border}`,
            bgcolor: "#FFFFFF",
          }}
        >
          <Typography
            fontSize={12}
            fontWeight={500}
            color={item.color}
            letterSpacing="0.06em"
            textTransform="uppercase"
            lineHeight={1.2}
            mb={0.25}
          >
            {item.label}
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" width="50%" height={22} />
          ) : (
            <Typography fontWeight={500} fontSize={16} color={item.color} lineHeight={1.2}>
              {formatCount(item.value)}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  </Box>
);

/**
 * Half-height status card (cols 3–4 stacks).
 */
const LifecycleMetricCard = ({ label, value, tooltip, color, border, isLoading = false }) => (
  <Box
    sx={{
      p: 2,
      border: `1px solid ${border}`,
      borderRadius: 1,
      boxShadow: 1,
      bgcolor: "#FFFFFF",
      flex: 1,
      display: "flex",
      flexDirection: "column",
      minHeight: 110,
    }}
  >
    <Box display="flex" alignItems="center" gap={0.75} mb={1}>
      <LifecycleDot color={color} />
      <Typography
        fontSize={12}
        fontWeight={500}
        color={COLORS.title}
        letterSpacing="0.04em"
        textTransform="uppercase"
        sx={{ flex: 1, lineHeight: 1.3 }}
      >
        {label}
      </Typography>
      <LifecycleInfoIcon title={tooltip} />
    </Box>
    {isLoading ? (
      <Skeleton variant="text" width="60%" height={30} sx={{ mt: "auto" }} />
    ) : (
      <Typography
        color={color}
        fontWeight={500}
        fontSize={24}
        lineHeight={1.15}
        mt="auto"
      >
        {value}
      </Typography>
    )}
  </Box>
);

const LatencyBadge = ({ value, danger }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      px: 1.25,
      py: 0.4,
      borderRadius: "999px",
      color: danger ? "#D72121" : "#009A67",
      bgcolor: danger ? "#FFF0F0" : "#EDFFF7",
      border: `1px solid ${danger ? "#FFAAAA" : "#72E3B9"}`,
      fontWeight: 500,
      fontSize: 12,
      whiteSpace: "nowrap",
    }}
  >
    {Number(value || 0).toFixed(2)} ms
  </Box>
);

const MethodBadge = ({ method }) => {
  const value = String(method || "GET").toUpperCase();
  const styles = {
    GET: { bgcolor: "#E1F5FF", color: "#6EADFC", border: "#6EADFC" },
    POST: { bgcolor: "#F5F3FF", color: "#C4B5FD", border: "#C4B5FD" },
    PUT: { bgcolor: "#FFF4E6", color: "#FFBC5D", border: "#FFBC5D" },
    PATCH: { bgcolor: "#FFF4E6", color: "#FFBC5D", border: "#FFBC5D" },
    DELETE: { bgcolor: "#FEF2F2", color: "#FCA5A5", border: "#FCA5A5" },
  };
  const tone = styles[value] || styles.GET;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 58,
        px: 1.25,
        py: 0.35,
        borderRadius: "999px",
        bgcolor: tone.bgcolor,
        color: tone.color,
        border: `1px solid ${tone.border}`,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {value}
    </Box>
  );
};

const StatusBadge = ({ code }) => {
  const success = Number(code) >= 200 && Number(code) < 300;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 48,
        px: 1.4,
        py: 0.35,
        borderRadius: "999px",
        bgcolor: success ? "#ECFDF5" : "#FEF2F2",
        color: success ? "#059669" : "#EF4444",
        border: `1px solid ${success ? "#6EE7B7" : "#FCA5A5"}`,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {code}
    </Box>
  );
};

/**
 * Pill badge for string webhook statuses (e.g. SUCCESS, TRANSMITTED, FAILED).
 * Unknown/lowercase values are normalised to uppercase and shown neutral.
 */
const WebhookStatusBadge = ({ value }) => {
  const label = String(value || "N/A").toUpperCase();
  const success = ["SUCCESS", "TRANSMITTED", "COMPLETED", "ACKNOWLEDGED", "DELIVERED"].includes(label);
  const danger = ["FAILED", "ERROR", "REJECTED", "TIMEOUT"].includes(label);
  const pending = ["PENDING", "IN_PROGRESS", "PROCESSING"].includes(label);
  const tone = danger
    ? { bgcolor: "#FEF2F2", color: "#EF4444", border: "#FCA5A5" }
    : pending
      ? { bgcolor: "#FFF7ED", color: "#EA580C", border: "#FDBA74" }
      : success
        ? { bgcolor: "#ECFDF5", color: "#059669", border: "#6EE7B7" }
        : { bgcolor: "#F3F3F5", color: "#4D4D68", border: "#D1D5DB" };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 58,
        px: 1.25,
        py: 0.35,
        borderRadius: "999px",
        ...tone,
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
};

export default ViewApiTransmissionPage;
